/**
 * BlockViewModal (ADR 0003 follow-up).
 *
 * Read-only view of a single saved block — the same labor / equipment /
 * materials detail the PM sees in the PDF Inbox, but with NO editing.
 *
 * Every action on a source block lives in this modal's footer (client,
 * 2026-07-29): View PDF far left, then Close · Transfer Block · Change date on
 * the right. The rollup's PDF Source Blocks row is a single "View" — the PM
 * opens the block, confirms it's the right one, and only then picks what to do
 * with it, instead of choosing from a row of buttons sight unseen.
 */
import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { InboxBlock } from '@coffman-tm/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatDateShort, formatHours, formatMoneyCents, gcName } from '../lib/format';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';

export interface BlockViewModalProps {
  open: boolean;
  dailyEntryId: string | null;
  /** False when the week is not open — moving blocks is locked. */
  canReassign: boolean;
  /** True only when the PCO is running more than one open submission — the
   *  same gate the row used to apply to its Transfer Block button. */
  canMoveToSubmission?: boolean;
  /** Source PDF for this block; null hides the View PDF link. */
  dailyPdfId?: string | null;
  /** Who/when from the caller's row. InboxBlock carries neither, so without
   *  these the title is just the activity code — and the header has
   *  nothing to show while the block fetch is in flight. */
  reportDate?: string | null;
  foremanName?: string | null;
  onViewPdf?: () => void;
  /** Hand off to the carve-out mover (Transfer Block). */
  onMoveToSubmission?: () => void;
  onClose: () => void;
  /** Hand off to the move flow for this block. */
  onReassign: () => void;
}

export function BlockViewModal({
  open,
  dailyEntryId,
  canReassign,
  canMoveToSubmission = false,
  dailyPdfId = null,
  reportDate = null,
  foremanName = null,
  onViewPdf,
  onMoveToSubmission,
  onClose,
  onReassign,
}: BlockViewModalProps) {
  const { token, me } = useAuth();
  const [block, setBlock] = useState<InboxBlock | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !dailyEntryId || !token) return;
    setLoading(true);
    setErr(null);
    setBlock(null);
    void (async () => {
      try {
        const res = await api.blocks.detail(token, dailyEntryId);
        setBlock(res.block);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Failed to load block');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, dailyEntryId, token]);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Who/when context from the caller's row, not the block fetch — lets the
  // title render before (or even if) the block body ever loads, and keeps
  // several open modals distinguishable when the PM is comparing blocks.
  const contextPrefix = [foremanName, reportDate ? formatDateShort(reportDate) : null]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return (
    <div
      className="pdf-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="block-view-modal-title"
    >
      <div
        className="confirm-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 920, width: '92vw' }}
      >
        <div className="confirm-modal-head">
          <div className="confirm-modal-title" id="block-view-modal-title">
            {contextPrefix && `${contextPrefix} — `}
            {block ? (
              <>
                <span className="mono">{block.activity_code}</span>
                {block.code_description ? ` — ${block.code_description}` : ''}
              </>
            ) : (
              'Block detail'
            )}
          </div>
        </div>
        <div className="confirm-modal-body" style={{ maxHeight: '64vh', overflowY: 'auto' }}>
          {loading ? (
            <p className="muted" style={{ fontSize: 13 }}>
              Loading…
            </p>
          ) : err ? (
            <p style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</p>
          ) : !block ? null : (
            <>
              {/* Labor */}
              <div className="muted" style={sectionLabel}>
                LABOR
              </div>
              {block.labor.length === 0 ? (
                <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                  No labor.
                </p>
              ) : (
                <table className="table" style={{ marginBottom: 14 }}>
                  <thead>
                    <tr>
                      <th>Emp ID</th>
                      <th>Name</th>
                      <th>Class</th>
                      <th className="num">Total hrs</th>
                      <th className="num">Rate</th>
                      <th className="num">Total pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {block.labor.map((l) => {
                      const hrs = l.confirmed_total_hours ?? l.parsed_total_hours;
                      const rate = l.confirmed_rate_cents ?? l.default_rate_cents;
                      return (
                        <tr key={l.daily_labor_entry_id}>
                          <td className="mono">{l.hj_id ?? '—'}</td>
                          <td>
                            {l.name}
                            {l.is_unknown && (
                              <span style={{ color: 'var(--warning)', marginLeft: 6, fontSize: 11 }}>
                                unknown
                              </span>
                            )}
                          </td>
                          <td className="muted">{l.classification}</td>
                          <td className="num">{formatHours(hrs)}</td>
                          <td className="num">{formatMoneyCents(rate, { precise: true })}</td>
                          <td className="num">{formatMoneyCents(Math.round(hrs * rate))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Equipment */}
              <div className="muted" style={sectionLabel}>
                EQUIPMENT
              </div>
              {block.equipment.length === 0 ? (
                <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                  No equipment.
                </p>
              ) : (
                <table className="table" style={{ marginBottom: 14 }}>
                  <thead>
                    <tr>
                      <th>Asset ID</th>
                      <th>Description</th>
                      <th className="num">Hrs</th>
                      <th className="num">Rate</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {block.equipment.map((e) => {
                      const hrs = e.confirmed_hours ?? e.parsed_hours;
                      const rate = e.confirmed_rate_cents ?? e.default_rate_cents;
                      return (
                        <tr key={e.daily_equipment_entry_id}>
                          <td className="mono">{e.asset_id}</td>
                          <td className="muted">{e.description}</td>
                          <td className="num">{formatHours(hrs)}</td>
                          <td className="num">{formatMoneyCents(rate, { precise: true })}</td>
                          <td className="num">{formatMoneyCents(Math.round(hrs * rate))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Materials (confirmed/edited only) */}
              {block.materials.some((m) => m.status === 'confirmed' || m.status === 'edited') && (
                <>
                  <div className="muted" style={sectionLabel}>
                    MATERIALS
                  </div>
                  <table className="table" style={{ marginBottom: 14 }}>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="num">Qty</th>
                        <th className="num">Rate</th>
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {block.materials
                        .filter((m) => m.status === 'confirmed' || m.status === 'edited')
                        .map((m) => {
                          const qty = m.confirmed_quantity ?? 0;
                          const rate = m.confirmed_rate_cents ?? 0;
                          return (
                            <tr key={m.material_queue_item_id}>
                              <td className="serif">{m.suggested_name ?? m.quoted_text}</td>
                              <td className="num">{qty}</td>
                              <td className="num">{formatMoneyCents(rate, { precise: true })}</td>
                              <td className="num">{formatMoneyCents(Math.round(qty * rate))}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </>
              )}

              {block.notes && (
                <>
                  <div className="muted" style={sectionLabel}>
                    NOTES
                  </div>
                  <p style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 4 }}>
                    {block.notes}
                  </p>
                </>
              )}
            </>
          )}
        </div>
        {/* Footer split: the source PDF sits far left (reference, not a
            decision), every action on this block groups right. The locked-week
            note takes the left slot when there is one, since in that state
            there are no move actions to explain. */}
        <div className="confirm-modal-actions">
          {/* Left slot. View PDF is not an action ON the block — it's the
              source document — so it renders whatever the week's state is. A
              sent week locks the move actions, not the paperwork; hiding the
              PDF there made it unreachable, since the row lost its own link. */}
          <div
            style={{
              marginRight: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minWidth: 0,
            }}
          >
            {dailyPdfId && (
              <button type="button" className="btn-link" onClick={onViewPdf}>
                View PDF
                <ArrowRight size={13} aria-hidden />
              </button>
            )}
            {!canReassign && (
              <span className="muted" style={{ fontSize: 11.5 }}>
                Reopen this week to move its blocks — it's already been sent to {gcName(me)}.
              </span>
            )}
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          {canMoveToSubmission && (
            <button
              type="button"
              className="btn btn-green"
              onClick={onMoveToSubmission}
              disabled={loading || !block || !canReassign}
            >
              Transfer Block
            </button>
          )}
          <button
            type="button"
            className="btn btn-cta"
            onClick={onReassign}
            disabled={loading || !block || !canReassign}
          >
            Change date
            <ArrowRight size={14} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  marginBottom: 4,
};
